import type { DraftSubprojectPayload } from '../../services/projectAutofillDraftService.ts'
import { isProjectAutofillAgentId } from '../../constants/agentNames.ts'

export type PublicationReadiness = {
  phase?: string
  projectAssignReady?: boolean
  subprojectPayloadReady?: boolean
  readyForCommitSequence?: boolean
  missingForProjectAssign?: string[]
  missingForSubprojectCreate?: string[]
  dateConstraintOk?: boolean
}

export type DraftValidationShape = {
  blockingIssues?: string[]
  warnings?: string[]
  publicationReadiness?: PublicationReadiness
}

export type DocumentExtractionSource = 'native' | 'ocr' | 'none' | string

export type DocumentExtractionMeta = {
  source?: DocumentExtractionSource
  needsOcr?: boolean
  truncated?: boolean
  originalCharCount?: number
  pageCount?: number
  parseError?: string
  ocrError?: string
}

export type DocumentationSlots = {
  budget?: number[]
  metricComputation?: number[]
  technicalSpecifications?: number[]
  planos?: number[]
}

export type DocumentationFileIds = {
  budget?: string
  metricComputation?: string
  technicalSpecifications?: string
  planos?: string
}

export type DraftMetadataFileEntry = {
  id?: string
  name?: string
  originalName?: string
  storageUrl?: string
  mimeType?: string
  tag?: string
}

export const DOCUMENTATION_SLOT_FILE_ACCEPT =
  '.pdf,.csv,.xlsx,.docx,.mp4,application/pdf,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.openxmlformats-officedocument.wordprocessingml.document,video/mp4'

export type DocumentationPreviewAssignment = {
  tempId?: string
  label?: string
  matchedSkillName?: string
  selectedSkillId?: number
  skillMatchKind?: string
  sourcePages?: number[]
  documentationSlots?: DocumentationSlots
}

export type DocumentationPreviewMeta = {
  assignments?: DocumentationPreviewAssignment[]
  pageCount?: number
  primaryStorageUrl?: string
  primaryFileId?: string
  status?: string
}

export const DOCUMENTATION_SLOT_KEYS: (keyof DocumentationSlots)[] = [
  'budget',
  'metricComputation',
  'technicalSpecifications',
  'planos'
]

export const DOCUMENTATION_SLOT_DEFINITIONS: {
  key: keyof DocumentationSlots
  label: string
  chipLabel: string
}[] = [
  { key: 'budget', label: 'Planilla de cotización', chipLabel: 'Presupuesto' },
  { key: 'metricComputation', label: 'Cómputo métrico', chipLabel: 'Cómputo' },
  { key: 'technicalSpecifications', label: 'Especificaciones técnicas', chipLabel: 'Especificaciones' },
  { key: 'planos', label: 'Planos', chipLabel: 'Planos' }
]

export type DocumentationPageRow = {
  page: number
  types: (keyof DocumentationSlots)[]
}

export const buildDocumentationPageRows = (
  slots: DocumentationSlots,
  fileIds: DocumentationFileIds
): DocumentationPageRow[] => {
  const map = new Map<number, Set<keyof DocumentationSlots>>()
  for (const key of DOCUMENTATION_SLOT_KEYS) {
    if (fileIds[key]) continue
    const pages = slots[key]
    if (!pages?.length) continue
    for (const p of pages) {
      if (!Number.isFinite(p) || p < 1) continue
      if (!map.has(p)) map.set(p, new Set())
      map.get(p)!.add(key)
    }
  }
  return Array.from(map.entries())
    .sort((a, b) => a[0] - b[0])
    .map(([page, typeSet]) => ({
      page,
      types: DOCUMENTATION_SLOT_KEYS.filter((k) => typeSet.has(k))
    }))
}

export const normalizePageList = (raw: unknown): number[] | undefined => {
  if (!Array.isArray(raw) || raw.length === 0) return undefined
  const nums = raw
    .map((n) => (typeof n === 'number' ? n : Number(n)))
    .filter((n) => Number.isFinite(n) && n >= 1)
  const unique = Array.from(new Set(nums)).sort((a, b) => a - b)
  return unique.length ? unique : undefined
}

export const normalizeDocumentationSlots = (raw: unknown): DocumentationSlots => {
  if (!raw || typeof raw !== 'object') return {}
  const o = raw as Record<string, unknown>
  return {
    budget: normalizePageList(o.budget),
    metricComputation: normalizePageList(o.metricComputation),
    technicalSpecifications: normalizePageList(o.technicalSpecifications),
    planos: normalizePageList(o.planos)
  }
}

export const normalizeDocumentationFileIds = (raw: unknown): DocumentationFileIds => {
  if (!raw || typeof raw !== 'object') return {}
  const o = raw as Record<string, unknown>
  const id = (v: unknown) => {
    const s = v != null ? String(v).trim() : ''
    return s.length > 0 ? s : undefined
  }
  return {
    budget: id(o.budget),
    metricComputation: id(o.metricComputation),
    technicalSpecifications: id(o.technicalSpecifications),
    planos: id(o.planos)
  }
}

export const documentationSlotsHavePages = (slots: DocumentationSlots): boolean =>
  DOCUMENTATION_SLOT_KEYS.some((k) => Array.isArray(slots[k]) && (slots[k] as number[]).length > 0)

export const isFullPageSelection = (pages: number[] | undefined, maxPage: number): boolean => {
  if (!Number.isFinite(maxPage) || maxPage < 1) return false
  const norm = normalizePageList(pages)
  if (!norm || norm.length !== maxPage) return false
  for (let i = 0; i < maxPage; i++) {
    if (norm[i] !== i + 1) return false
  }
  return true
}

export type RubroDocumentationResolved = {
  slots: DocumentationSlots
  sourcePages?: number[]
}

export const resolveRubroDocumentation = (
  tempId: string,
  documentationPreview: DocumentationPreviewMeta | null | undefined,
  draftSubprojects:
    | Array<{
        tempId?: string
        documentationSlots?: DocumentationSlots
        sourcePages?: number[]
      }>
    | undefined
): RubroDocumentationResolved => {
  const subs = Array.isArray(draftSubprojects) ? draftSubprojects : []
  const sp = subs.find((s) => String(s?.tempId ?? '') === String(tempId))
  if (sp) {
    return {
      slots: normalizeDocumentationSlots(sp.documentationSlots),
      sourcePages: Array.isArray(sp.sourcePages) ? normalizePageList(sp.sourcePages) : undefined
    }
  }
  const assignments = documentationPreview?.assignments
  if (Array.isArray(assignments) && assignments.length > 0) {
    const a = assignments.find((x) => String(x?.tempId ?? '') === String(tempId))
    if (a) {
      return {
        slots: normalizeDocumentationSlots(a.documentationSlots),
        sourcePages: Array.isArray(a.sourcePages) ? normalizePageList(a.sourcePages) : undefined
      }
    }
  }
  return { slots: {} }
}

export function getEffectiveDocumentationPageCount(
  documentationPreview: DocumentationPreviewMeta | null | undefined,
  draftMetadata:
    | {
        pageCount?: number
      }
    | null
    | undefined
): number | null {
  const fromPreview = documentationPreview?.pageCount
  if (typeof fromPreview === 'number' && Number.isFinite(fromPreview) && fromPreview >= 1) {
    return fromPreview
  }
  const fromMeta = draftMetadata?.pageCount
  if (typeof fromMeta === 'number' && Number.isFinite(fromMeta) && fromMeta >= 1) {
    return fromMeta
  }
  return null
}

export function extractDraftMetadataFilesFromResponse(res: unknown): DraftMetadataFileEntry[] | null {
  if (!res || typeof res !== 'object') return null
  const r = res as Record<string, unknown>
  const fromContainer = (obj: Record<string, unknown> | null): DraftMetadataFileEntry[] | null => {
    if (!obj) return null
    const meta = obj.metadata && typeof obj.metadata === 'object' ? (obj.metadata as Record<string, unknown>) : null
    const files = meta?.files
    return Array.isArray(files) ? (files as DraftMetadataFileEntry[]) : null
  }
  const rootFiles = fromContainer(r as Record<string, unknown>)
  if (rootFiles) return rootFiles
  const data = r.data && typeof r.data === 'object' ? (r.data as Record<string, unknown>) : r
  const direct = fromContainer(data as Record<string, unknown>)
  if (direct) return direct
  const draft = data.draft && typeof data.draft === 'object' ? (data.draft as Record<string, unknown>) : null
  return fromContainer(draft)
}

export function pickNewFileIdAfterUpload(
  previous: DraftMetadataFileEntry[] | undefined,
  next: DraftMetadataFileEntry[] | undefined
): string | undefined {
  const prev = new Set((previous || []).map((f) => (f?.id != null ? String(f.id) : '')).filter(Boolean))
  for (const f of next || []) {
    const id = f?.id != null ? String(f.id) : ''
    if (id && !prev.has(id)) return id
  }
  return undefined
}

export type ProjectAutofillPayload = {
  actionId?: string
  activeAgent?: string
  draftId?: string
  nextAction?: string
  collectedData?: { requiredFields?: string[]; draftId?: string; draftStatus?: string; nextAction?: string }
  validation?: DraftValidationShape
  draft?: {
    id?: string
    status?: string
    metadata?: {
      nameSource?: string
      endDateSource?: string
      descriptionSource?: string
      discardedPastProjectDates?: boolean
      documentExtraction?: DocumentExtractionMeta
      documentationPreview?: DocumentationPreviewMeta
      analysisError?: string
      pageCount?: number
      files?: DraftMetadataFileEntry[]
      publicationResult?: { proyectoId?: string }
    }
    validation?: DraftValidationShape
    project?: {
      name?: string
      description?: string
      zone?: string
      startDate?: string
      endDate?: string
    }
    subprojects?: Array<{
      tempId?: string
      detectedLabel?: string
      selectedSkillId?: number
      description?: string
      status?: string
      skillMatchKind?: string
      matchedSkillName?: string
      matchScore?: number
      name?: string
      link?: string
      startDate?: string
      endDate?: string
      datesInheritedFromProject?: boolean
      sourcePages?: number[]
      documentationSlots?: DocumentationSlots
      documentationFileIds?: DocumentationFileIds
    }>
  }
}

export type SkillOption = {
  id: number
  name: string
}

export type RubroRow = {
  tempId: string
  detectedLabel: string
  selectedSkillId?: number
  description: string
  status: 'OPEN' | 'CLOSE'
  skillMatchKind?: string
  matchedSkillName?: string
  matchScore?: number
  name?: string
  link?: string
  startDate?: string
  endDate?: string
  datesInheritedFromProject?: boolean
  documentationSlots?: DocumentationSlots
  sourcePages?: number[]
  documentationFileIds?: DocumentationFileIds
  manualAdd?: boolean
}

export function rubroHasValidSelectedSkill(r: RubroRow): boolean {
  const id = r.selectedSkillId
  return typeof id === 'number' && Number.isFinite(id) && id > 0
}

export function rubroNeedsMasterPdfForCrop(r: RubroRow): boolean {
  const ids = normalizeDocumentationFileIds(r.documentationFileIds ?? {})
  const slots = normalizeDocumentationSlots(r.documentationSlots ?? {})
  return DOCUMENTATION_SLOT_KEYS.some((k) => {
    const hasFile = typeof ids[k] === 'string' && String(ids[k]).trim().length > 0
    const pages = slots[k]
    const hasPages = Array.isArray(pages) && pages.length > 0
    return hasPages && !hasFile
  })
}

function rubroHasInvalidDocumentationPagesForRow(r: RubroRow, maxPage: number): boolean {
  const fileIds = normalizeDocumentationFileIds(r.documentationFileIds ?? {})
  const norm = normalizeDocumentationSlots(r.documentationSlots ?? {})
  for (const k of DOCUMENTATION_SLOT_KEYS) {
    const fid = fileIds[k]
    if (typeof fid === 'string' && fid.trim().length > 0) continue
    const arr = norm[k]
    if (!Array.isArray(arr)) continue
    for (const p of arr) {
      if (typeof p !== 'number' || !Number.isFinite(p) || p < 1 || p > maxPage) return true
    }
  }
  if (Array.isArray(r.sourcePages)) {
    for (const p of r.sourcePages) {
      if (typeof p !== 'number' || !Number.isFinite(p) || p < 1 || p > maxPage) return true
    }
  }
  return false
}

export function getRubroTempIdsWithDocumentationPageIssues(rows: RubroRow[], maxPage: number): string[] {
  if (!Number.isFinite(maxPage) || maxPage < 1) return []
  const out: string[] = []
  for (let idx = 0; idx < rows.length; idx++) {
    const r = rows[idx]
    const tid = r.tempId || `sp-${idx}`
    if (rubroHasInvalidDocumentationPagesForRow(r, maxPage)) out.push(tid)
  }
  return out
}

export const validateRubrosDocumentationPages = (
  rows: RubroRow[],
  maxPage: number
): string | null => {
  if (!Number.isFinite(maxPage) || maxPage < 1) return null
  for (const r of rows) {
    if (!rubroHasInvalidDocumentationPagesForRow(r, maxPage)) continue
    const fileIds = normalizeDocumentationFileIds(r.documentationFileIds ?? {})
    const norm = normalizeDocumentationSlots(r.documentationSlots ?? {})
    for (const k of DOCUMENTATION_SLOT_KEYS) {
      const fid = fileIds[k]
      if (typeof fid === 'string' && fid.trim().length > 0) continue
      const arr = norm[k]
      if (!Array.isArray(arr)) continue
      for (const p of arr) {
        if (typeof p !== 'number' || !Number.isFinite(p) || p < 1 || p > maxPage) {
          return `P\u00e1gina inv\u00e1lida (${p}) en "${r.detectedLabel || r.tempId}": use n\u00fameros entre 1 y ${maxPage}.`
        }
      }
    }
    if (Array.isArray(r.sourcePages)) {
      for (const p of r.sourcePages) {
        if (typeof p !== 'number' || !Number.isFinite(p) || p < 1 || p > maxPage) {
          return `P\u00e1gina inv\u00e1lida (${p}) en p\u00e1ginas relevantes de "${r.detectedLabel || r.tempId}": use n\u00fameros entre 1 y ${maxPage}.`
        }
      }
    }
  }
  return null
}

export const shallowMergeDraftMetadataForPatch = (
  metadata: ProjectAutofillPayload['draft'] extends { metadata?: infer M } ? M : never
): Record<string, unknown> => {
  const base =
    metadata && typeof metadata === 'object' && !Array.isArray(metadata)
      ? ({ ...(metadata as Record<string, unknown>) } as Record<string, unknown>)
      : {}
  return { ...base, source: 'frontend' }
}

export const serializeRubrosSnapshot = (rows: RubroRow[]) =>
  JSON.stringify(
    [...rows]
      .sort((a, b) => a.tempId.localeCompare(b.tempId))
      .map((r) => ({
        tempId: r.tempId,
        detectedLabel: r.detectedLabel,
        matchedSkillName: r.matchedSkillName,
        selectedSkillId: r.selectedSkillId,
        manualAdd: r.manualAdd === true,
        description: r.description,
        status: r.status,
        startDate: r.startDate,
        endDate: r.endDate,
        datesInheritedFromProject: r.datesInheritedFromProject,
        documentationSlotsKey: DOCUMENTATION_SLOT_KEYS.map((k) => {
          const arr = r.documentationSlots?.[k]
          return `${String(k)}:${Array.isArray(arr) ? [...arr].sort((x, y) => x - y).join(',') : ''}`
        }).join(';'),
        documentationFileIdsKey: DOCUMENTATION_SLOT_KEYS.map((k) => {
          const id = r.documentationFileIds?.[k]
          return `${String(k)}:${id != null ? String(id) : ''}`
        }).join(';'),
        sourcePagesKey: (r.sourcePages || []).slice().sort((x, y) => x - y).join(',')
      }))
  )

export type CommittedDraftSnapshot = {
  name: string
  description: string
  zone: string
  startDate: string
  endDate: string
  rubrosKey: string
  rubrosCount: number
}

export const mergeValidationWarnings = (a?: string[], b?: string[]) => {
  const out: string[] = []
  const seen = new Set<string>()
  for (const list of [a, b]) {
    for (const raw of list || []) {
      const w = String(raw || '').trim()
      if (!w || seen.has(w)) continue
      seen.add(w)
      out.push(w)
    }
  }
  return out
}

export const EMPTY_REQUIRED_FIELDS: string[] = []

export const READINESS_MISSING_LABELS: Record<string, string> = {
  'project.name': 'Nombre del proyecto',
  'project.description': 'Descripcion del proyecto',
  'project.startDate': 'Fecha de inicio del proyecto',
  'project.endDate': 'Fecha de finalizacion del proyecto',
  'project.zone': 'Ubicacion de la obra',
  subprojects: 'Rubros a cotizar',
  'subproject.selectedSkillId': 'Rubro con skill elegido',
  'subproject.detectedLabel': 'Etiqueta del rubro',
  dateConstraintOk: 'Fechas coherentes (inicio y fin)'
}

export const formatReadinessKeys = (keys?: string[]) =>
  (keys || []).map((k) => READINESS_MISSING_LABELS[k] || k.replace(/_/g, ' '))

export const humanizeDraftStatus = (status?: string) => {
  const normalized = String(status || '').trim().toLowerCase()
  const map: Record<string, string> = {
    draft: 'Borrador',
    in_review: 'En revision',
    inreview: 'En revision',
    ready: 'Listo',
    completed: 'Completado'
  }
  if (map[normalized]) return map[normalized]
  if (!normalized) return 'Borrador'
  return normalized.replace(/_/g, ' ').replace(/\b\w/g, (m) => m.toUpperCase())
}

export const clampIsoDateToMax = (d: string, maxStr: string) => {
  if (!d || !maxStr) return d
  return d > maxStr ? maxStr : d
}

export const mapRubrosToDraftPayload = (
  rows: RubroRow[],
  resolveSkillName?: (skillId: number) => string | undefined
): DraftSubprojectPayload[] =>
  rows.map((r) => {
    const row: DraftSubprojectPayload = {
      tempId: r.tempId,
      detectedLabel: r.detectedLabel || undefined,
      description: r.description || undefined,
      status: r.status,
      startDate: r.startDate || undefined,
      endDate: r.endDate || undefined
    }
    if (rubroHasValidSelectedSkill(r)) {
      row.selectedSkillId = r.selectedSkillId
    }
    const sid = rubroHasValidSelectedSkill(r) ? r.selectedSkillId : undefined
    const fromCatalog =
      typeof sid === 'number' ? String(resolveSkillName?.(sid) || '').trim() : ''
    const fromRow = String(r.matchedSkillName || '').trim()
    const realSkillName = fromCatalog || fromRow
    if (realSkillName) {
      row.matchedSkillName = realSkillName
    }
    if (r.datesInheritedFromProject === true || r.datesInheritedFromProject === false) {
      row.datesInheritedFromProject = r.datesInheritedFromProject
    }
    const norm = normalizeDocumentationSlots(r.documentationSlots ?? {})
    const slotsOut: NonNullable<DraftSubprojectPayload['documentationSlots']> = {}
    for (const k of DOCUMENTATION_SLOT_KEYS) {
      const arr = norm[k]
      if (Array.isArray(arr) && arr.length > 0) {
        slotsOut[k] = [...arr]
      }
    }
    row.documentationSlots = slotsOut
    if (r.sourcePages && r.sourcePages.length > 0) {
      row.sourcePages = [...r.sourcePages]
    }
    const idsNorm = normalizeDocumentationFileIds(r.documentationFileIds ?? {})
    const idsOut: NonNullable<DraftSubprojectPayload['documentationFileIds']> = {}
    for (const k of DOCUMENTATION_SLOT_KEYS) {
      const id = idsNorm[k]
      if (typeof id === 'string' && id.length > 0) {
        idsOut[k] = id
      }
    }
    if (Object.keys(idsOut).length > 0) {
      row.documentationFileIds = idsOut
    }
    return row
  })

export const validationBundleFromPatchResponse = (
  res: unknown
):
  | {
      validation?: DraftValidationShape
      collectedData?: ProjectAutofillPayload['collectedData']
    }
  | null => {
  if (!res || typeof res !== 'object') return null
  const raw = res as Record<string, unknown>
  const inner =
    raw.data && typeof raw.data === 'object'
      ? (raw.data as Record<string, unknown>)
      : raw
  const nestedDraft =
    inner.draft && typeof inner.draft === 'object'
      ? (inner.draft as Record<string, unknown>)
      : null

  const v =
    (inner.validation as DraftValidationShape | undefined) ??
    (nestedDraft?.validation as DraftValidationShape | undefined)
  let cd =
    (inner.collectedData as ProjectAutofillPayload['collectedData'] | undefined) ??
    (nestedDraft?.collectedData as ProjectAutofillPayload['collectedData'] | undefined)

  const statusVal = inner.status ?? nestedDraft?.status
  if (typeof statusVal === 'string' || typeof statusVal === 'number') {
    cd = {
      ...(cd && typeof cd === 'object' ? cd : {}),
      draftStatus: String(statusVal)
    }
  }

  const hasV = v != null && typeof v === 'object'
  const hasCd = cd != null && typeof cd === 'object' && Object.keys(cd).length > 0
  if (!hasV && !hasCd) return null

  return {
    validation: hasV ? v : undefined,
    collectedData: hasCd ? cd : undefined
  }
}

export const parseProjectAutofill = (llmRawResponse?: string): ProjectAutofillPayload | null => {
  if (!llmRawResponse) return null
  try {
    const parsed = JSON.parse(llmRawResponse) as ProjectAutofillPayload
    const isProjectAutofill =
      parsed.actionId === 'project-autofill' || isProjectAutofillAgentId(parsed.activeAgent)
    if (!isProjectAutofill) return null
    const hasDraftId = !!(parsed.draftId || parsed.collectedData?.draftId || parsed.draft?.id)
    const hasDraftContent = !!parsed.draft
    if (!hasDraftId && !hasDraftContent) return null
    return parsed
  } catch {
    return null
  }
}
